<template>
    <app-layout title="Create Recurring Order">
        <form @submit.prevent="handleSubmit">
            <page-header>
                <template #title>Create Recurring Order</template>

                <template #actions>
                    <jet-button v-show="form.isDirty" type="submit" class="leading-none" :disabled="form.processing">
                        Save
                    </jet-button>

                    <jet-tertiary-button
                        v-if="form.isDirty"
                        :disabled="form.processing"
                        class="leading-none"
                        @click="showUnsavedChangesModal = true"
                    >
                        Cancel
                    </jet-tertiary-button>

                    <jet-tertiary-button
                        v-else
                        :disabled="form.processing"
                        :href="route('recurring_orders.index')"
                        class="leading-none"
                    >
                        Close
                    </jet-tertiary-button>
                </template>
            </page-header>

            <customer-form
                :customers="customers"
                :errors="errors"
                :form="form"
                :is-admin="isAdmin"
                :active-customer="activeCustomer"
                class="px-3 md:px-4 lg:px-8"
            />

            <div class="px-3 md:px-4 lg:px-8 py-4">
                <order-items-edit
                    :form="form"
                    :customers="customers"
                    v-show="form.customer_id"
                    :is-admin="isAdmin"
                    :active-customer="activeCustomer"
                />
            </div>
        </form>

        <unsaved-changes-modal-2
            :formRoute="formRoute"
            :isDirty="form.isDirty"
            :show="showUnsavedChangesModal"
            @close="showUnsavedChangesModal = false"
            @confirm="$inertia.get(backUrl)"
        />
    </app-layout>
</template>

<script>
import { useForm, usePage } from '@inertiajs/inertia-vue3';
import { ref } from 'vue';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import CustomerForm from '@/Pages/RecurringOrders/Partials/CustomerForm.vue';
import OrderItemsEdit from '@/Pages/RecurringOrders/Partials/OrderItemsEdit';
import JetButton from '@/Jetstream/Button';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetLabel from '@/Jetstream/Label';
import JetInputError from '@/Jetstream/InputError';
import Multiselect from '@vueform/multiselect';
import Datepicker from '@vuepic/vue-datepicker';
import UnsavedChangesModal2 from '@/Shared/UnsavedChangesModal2';
import UseUserAuth from '@/Composables/UseUserAuth'
import { DateTime } from 'luxon';

import '@vueform/multiselect/themes/default.css';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
    props: ['customers', 'errors'],

    components: {
        AppLayout,
        PageHeader,
        CustomerForm,
        OrderItemsEdit,
        JetButton,
        JetTertiaryButton,
        JetLabel,
        JetInputError,
        Multiselect,
        Datepicker,
        UnsavedChangesModal2,
    },

    setup() {
        const { isAdmin } = UseUserAuth(usePage().props.value.user)
        const activeCustomer = ref(usePage().props.value.activeCustomer || null);

        const initialShipping = isAdmin.value
            ? 'delivery'
            : (activeCustomer.value?.default_shipping_method || 'delivery');

        const form = useForm({
            id: null,
            customer_id: isAdmin.value ? null : activeCustomer.value?.id || null,
            start_date: new Date(),
            frequency: 'weekly',
            next_order_date: null,
            order_date: new Date(),
            ship_date: null,
            payment_status: 'unpaid',
            shipping_method: initialShipping,
            override_ship_date: false,
            delivery_fee: null,
            applied_credit_amount: null,
            override_stock_restrictions: false,
            order_items: [],
            schedule_days: [],
            schedule_frequency: 1,
            schedule_units: 'weeks',
            schedule_ordinance: [],
            weeks_in_advance: 1,
            delivery_fee: null,
            next_generation_date: null,
        });

        return { form, isAdmin, activeCustomer };
    },

    data() {
        return {
            formRoute: this.route('recurring_orders.store'),
            showUnsavedChangesModal: false,
            frequencyOptions: [
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'biweekly', label: 'Every 2 Weeks' },
                { value: 'monthly', label: 'Monthly' },
            ],
        };
    },

    methods: {
        handleSubmit() {
            if (!this.form.processing) {
                this.form
                    .transform((data) => ({
                        ...data,
                        order_items: data.order_items.map(({ product, customer_price, customer_split_case_price, last_quantity_for_price, ...item }) => item),
                    }))
                    .post(route('recurring_orders.store'));
            }
        },

        formatDate(date) {
            return DateTime.fromJSDate(date).toSQLDate();
        },
    },

    computed: {
        backUrl() {
            return route('recurring_orders.index');
        },

        activeCustomer() {
            return this.$page.props.activeCustomer;
        }
    },
};
</script>
